import Select from 'domains/core/components/Select';
import { ApplicationAccessType } from 'domains/content/types';
import { Box, Checkbox, ListItemText, Stack, Typography } from '@mui/material';
import useApplicationsAccessList from 'hooks/queries/useApplicationsAccessList';
import { DynamicContentValidationMessage } from '../ContentTaggingButtonWithDialog';

const applicationsAccessLabelMap: Record<string, string> = {
    outreach: 'Outreach',
    in_care: 'InCare',
    in_journey: 'InJourney',
    access: 'Access',
};

type Props = {
    handleApplicationsAccessSelect: (event: string[]) => void;
    validationErrorMessage?: string;
    selectedApplicationsAccess: ApplicationAccessType[];
    hasDynamicContent?: boolean;
    channel?: string;
};

const ApplicationsAccessOptionSelect = ({
    handleApplicationsAccessSelect,
    validationErrorMessage,
    selectedApplicationsAccess,
    hasDynamicContent = false,
    channel,
}: Props) => {
    const { data } = useApplicationsAccessList();

    // When dynamic content is present in EMAIL, only allow Outreach
    const isEmailChannel = channel === 'Email';
    const shouldDisableNonOutreach = hasDynamicContent && isEmailChannel;

    const applicationsAccessOptions = data?.map((value) => ({
        label: applicationsAccessLabelMap[value],
        value,
        disabled: shouldDisableNonOutreach && value !== ApplicationAccessType.OUTREACH,
    }));

    const handleChange = (value: unknown) => {
        const selected = typeof value === 'string' ? value.split(',') : (value as string[]);
        handleApplicationsAccessSelect(selected);
    };

    const dynamicContentMessage = hasDynamicContent && isEmailChannel ? DynamicContentValidationMessage : '';

    return (
        <Stack spacing={1}>
            <Select
                data-testid="applications-access-select"
                error={!!validationErrorMessage}
                multiple
                fullWidth
                label="Applications Access"
                onChange={handleChange}
                type="string"
                value={selectedApplicationsAccess}
                values={applicationsAccessOptions}
                renderValue={(selected: any) => {
                    const selectedValues = selected as string[];
                    const selectedLabels = applicationsAccessOptions
                        ?.filter((option) => selectedValues.includes(option.value))
                        .map((option) => option.label);
                    return selectedLabels?.join(', ');
                }}
                customOptionRender={(option, selectedValues) => (
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Checkbox
                            checked={selectedValues?.indexOf(option.value) > -1}
                            sx={{
                                padding: '4px',
                                ...(option.disabled && { opacity: 0.5 }),
                            }}
                            disabled={option.disabled}
                        />
                        <ListItemText
                            primary={option.label}
                            sx={{
                                margin: 0,
                                ...(option.disabled && { opacity: 0.5 }),
                            }}
                        />
                    </Stack>
                )}
            />
            {validationErrorMessage && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                    <Typography
                        data-testid="helper-text"
                        variant="body2"
                        color="error"
                        sx={{
                            fontSize: '0.75rem',
                        }}
                    >
                        {validationErrorMessage}
                    </Typography>
                </Box>
            )}
            {dynamicContentMessage && (
                <Typography
                    data-testid="helper-text"
                    variant="body2"
                    color="text.secondary"
                    sx={{ display: 'flex', justifyContent: 'flex-end', fontSize: '0.75rem' }}
                >
                    {dynamicContentMessage}
                </Typography>
            )}
        </Stack>
    );
};

export default ApplicationsAccessOptionSelect;
